import ApiService from '../../services/api.service'
import { Notify } from 'quasar'

/**
 * Calcule le score d'une unité basé sur les scores de ses compétences.
 * @param {Object} evaluationsContent - Objet contenant tous les contenus d'évaluation.
 * @param {String} uniteCompetenceId - ID de l'unité de compétence.
 * @param {Number} maxScore - Score max attendu (DIPLOMANTE=3, NON DIPLOMANTE=4).
 * @returns {Number} Score calculé (0-maxScore).
 */
function calculateUnitScoreFromContent(
  evaluationsContent,
  uniteCompetenceId,
  maxScore = 3
) {
  //Filtrer les compétences (SKILL) de cette unité.
  const skills = Object.values(evaluationsContent).filter(
    (content) =>
      content.type_node === 'SKILL' &&
      content.unite_competence === uniteCompetenceId
  )
  if (skills.length === 0) return 0

  //Récupérer les scores.
  // Sécurise le calcul si certains écrans stockent encore un score > maxScore.
  const scores = skills.map((skill) => {
    const raw = Number(skill.score) || 0
    return Math.max(0, Math.min(raw, maxScore))
  })
  const evaluatedScores = scores.filter((score) => score > 0)

  if (evaluatedScores.length === 0) return 0
  const minScore = Math.min(...evaluatedScores)
  const avgScore =
    evaluatedScores.reduce((a, b) => a + b, 0) / evaluatedScores.length

  //Logique de calcul.
  // - DIPLOMANTE: 0..3
  // - NON DIPLOMANTE: 0..4
  // On utilise des seuils par paliers: maxScore, maxScore-1, ...
  if (minScore === maxScore && evaluatedScores.length === skills.length) {
    return maxScore // Toutes au palier max.
  }

  if (minScore >= maxScore - 1 && avgScore >= maxScore - 1) {
    return maxScore - 1
  }

  if (minScore >= maxScore - 2 && avgScore >= maxScore - 2) {
    return maxScore - 2
  }

  if (minScore >= 1) {
    return 1
  }

  return 0
}

export const antenneEvaluations = {
  state: {
    evaluations: [],
    evaluationContent: [],
    evaluation: null,
    objet: null
  },
  mutations: {
    setEvaluations(state, evaluations) {
      state.evaluations = evaluations
    },
    setEvaluation(state, evaluation) {
      state.evaluation = evaluation
    },
    callbackAddEvaluation(state, payload) {
      state.objet = payload.objet
    },
    callbackAddEvaluationContent(state, evaluationContent) {
      state.evaluationContent = evaluationContent
    },
    callbackEditEvaluation(state, payload) {
      state.objet = payload.objet
    }
  },
  actions: {
    //  * Récupération du content d'une évaluation (pour palier au jsonResponse vide du controleur) - à débugger absolument !
    //==============================================
    // RÉCUPÉRATION DES DONNÉES
    //==============================================

    /**
     * Récupère le contenu d'une évaluation.
     */
    fetchEvaluationContent(context, antenneEvaluation) {
      return ApiService.getRequest(
        `/antenne_evaluation_contents?antenneEvaluation=${antenneEvaluation}`
      )
        .then((response) => {
          this.commit(
            'callbackAddEvaluationContent',
            response.data['hydra:member']
          )
        })
        .catch((error) => {
          console.error(error)
        })
    },

    /* CRUD EVALUATIONS/TESTS (POST/PATCH/DELETE) */
    //  Ajouter une évaluation en 4 étapes
    async addEvaluation({ state, dispatch }, data) {
      try {
        let evaluationsContent = {}
        let document = null
        let evaluationHaveContent = false
        let evaluationHaveDocument = false
        const IS_ACTIVE = 0

        const maxUnitScore = data.typeActionFormation === 'NON DIPLOMANTE' ? 4 : 3

        document = data.antenne_evaluation_documents
        if (
          document !== undefined &&
          Object.keys(document).length > 0 &&
          document.file !== null
        ) {
          evaluationHaveDocument = true
        }
        delete data.antenne_evaluation_documents

        evaluationsContent = data.evaluations_content
        if (
          Object.keys(data.evaluations_content).length > 0 ||
          data.type === 'CPS'
        ) {
          evaluationHaveContent = true
        }

        const sequenceContent = data.selectedSequenceContent || []
        const units = sequenceContent.filter(
          (content) => content.type_node === 'UNIT'
        )

        //Pour chaque UNIT, calculer son score et l'ajouter à evaluationsContent.
        units.forEach((unit) => {
          const unitScore = calculateUnitScoreFromContent(
            evaluationsContent,
            unit.unite_competence['@id'] || unit.unite_competence,
            maxUnitScore
          )

          //Ajouter l'UNIT avec son score calculé.
          const unitKey = unit.id
          evaluationsContent[unitKey] = {
            antenne_sequence_content: unit['@id'],
            antenne_referentiel:
              unit.antenne_referentiel['@id'] || unit.antenne_referentiel,
            type_node: 'UNIT',
            label: unit.label,
            unite_competence:
              unit.unite_competence['@id'] || unit.unite_competence,
            competence: null,
            descripteur: null,
            score: unitScore,
            date_evaluation: data.date_evaluation,
            commentaire: '', //Pas de commentaire pour les UNIT calculées.
            etat: IS_ACTIVE
          }
        })

        delete data.evaluations_content
        delete data.selectedSequenceContent
        delete data.typeActionFormation

        //==============================================
        //CRÉATION DE L'ÉVALUATION PRINCIPALE
        //==============================================
        const response = await ApiService.postRequest(
          'antenne_evaluations',
          data
        )

        if (response.status === 201) {
          let antenneEvaluationId = response.data.id

          if (evaluationHaveDocument) {
            document['evaluation'] = '/antenne_evaluations/' + response.data.id
            await dispatch('transformDataToForm', document)
          }

          if (evaluationHaveContent) {
            let contentPromises = []
            for (let key in evaluationsContent) {
              if (evaluationsContent.hasOwnProperty(key)) {
                evaluationsContent[key]['antenne_referentiel'] =
                  evaluationsContent[key]['antenne_referentiel']['@id']
                    ? evaluationsContent[key]['antenne_referentiel']['@id']
                    : evaluationsContent[key]['antenne_referentiel']
                evaluationsContent[key]['antenne_evaluation'] =
                  '/antenne_evaluations/' + response.data.id
                evaluationsContent[key]['etat'] = IS_ACTIVE

                contentPromises.push(
                  dispatch(
                    'transformDataContentToForm',
                    evaluationsContent[key],
                    response.data.id
                  )
                )
              }
            }
            await Promise.all(contentPromises)
          }

          this.commit('callbackAddEvaluation', response.data)
          Notify.create({
            message: "L'évaluation a été ajoutée avec succès.",
            color: 'green'
          })
          return Promise.resolve()
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        return Promise.reject(error)
      }
    },
    //  * transforme un objet en formulaire de données
    async transformDataContentToForm({ state, dispatch }, data) {
      try {
        let form = new FormData()
        form.append('antenne_evaluation', data.antenne_evaluation)
        form.append('sequence_content', data.antenne_sequence_content)
        form.append('antenne_referentiel', data.antenne_referentiel)

        if (data.unite_competence) {
          form.append('unite_competence', data.unite_competence)
        }
        if (data.competence) {
          form.append('competence', data.competence)
        }
        if (data.descripteur) {
          form.append('descripteur', data.descripteur)
        }

        form.append('type_node', data.type_node)
        form.append('label', data.label)
        form.append('score', data.score ?? '')

        if (data.date_evaluation) {
          form.append('date_evaluation', data.date_evaluation)
        }

        form.append('etat', data.etat)

        if (data.commentaire) {
          form.append('commentaire', data.commentaire)
        }

        await dispatch('addEvaluationContent', form, { root: true })
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        return Promise.reject(error)
      }
    },
    //  * ajoute les nodes cochés dans le formulaire
    async addEvaluationContent({ state }, data) {
      return new Promise((resolve, reject) => {
        ApiService.postRequest('antenne_evaluation_contents', data)
          .then((response) => {
            this.commit('callbackAddEvaluation', response.data)
            Notify.create({
              message: "Le noeud d'évaluation a été ajouté avec succès.",
              color: 'green'
            })
            resolve()
          })
          .catch((error) => {
            console.error(error)
            Notify.create({
              message: "Une erreur s'est produite",
              color: 'red'
            })
            reject(error)
          })
      })
    },
    //  * ajoute les nodes manquants si non cochés puis les récupère en bdd - problème avec le retour du jsonResponse du controlleur
    async AddEvaluationContentParentNode({ state, dispatch }, uriEvaluation) {
      //   console.log('Payload envoyé pour add_antenne_evaluation_contents:', {
      //     id: uriEvaluation
      //   })

      const payload = { id: uriEvaluation }

      const response = await ApiService.postRequest(
        'add_antenne_evaluation_contents',
        payload
      )

      if (response.status === 200) {
        const responseFetch = await dispatch(
          'fetchEvaluationContent',
          `/antenne_evaluations/${uriEvaluation}`
        )
        this.commit('callbackAddEvaluationContent', responseFetch)
        return Promise.resolve()
      } else {
        throw new Error('Une erreur est survenue')
      }
    },

    //==============================================
    // ÉDITION ET GESTION D'ÉTAT
    //==============================================

    /**
     * Modifie les informations d'une évaluation.
     */
    async editEvaluationInformations(context, formData) {
      try {
        const response = await ApiService.patchRequest(
          `antenne_evaluations/${formData.id}`,
          {
            evaluated_by: formData.evaluated_by,
            date_evaluation: formData.date_evaluation,
            absent: formData.absent,
            label: formData.label,
            global_score: formData.global_score,
            commentaire: formData.commentaire
          }
        )

        if (response.status === 200) {
          this.commit('callbackEditEvaluation', response.data)
          Notify.create({
            message: "L'évaluation a été modifiée avec succès",
            color: 'green'
          })
          return Promise.resolve()
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        return Promise.reject(error)
      }
    },

    /**
     * Change le statut d'une évaluation (archiver/désarchiver/supprimer).
     */
    async statusEvaluation(context, formData) {
      try {
        const response = await ApiService.patchRequest(`${formData.id}`, {
          etat: formData.etat
        })
        if (response.status === 200) {
          Notify.create({
            message: formData.message,
            color: 'green'
          })
          return Promise.resolve()
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        return Promise.reject(error)
      }
    },

    /**
     * Modifie le contenu d'une évaluation (scores, commentaires).
     */
    async editEvaluationContent(_, contentData) {
      try {
        const response = await ApiService.patchRequest(
          `antenne_evaluation_contents/${contentData.id}`,
          {
            score: contentData.score,
            commentaire: contentData.commentaire,
            date_evaluation: contentData.date_evaluation
          }
        )

        if (response.status === 200) {
          Notify.create({
            message: 'Le score a été modifié avec succès',
            color: 'green'
          })
          return Promise.resolve()
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite lors de la modification du score",
          color: 'red'
        })
        return Promise.reject(error)
      }
    },

    /**
     * Supprime une évaluation.
     */
    async deleteEvaluation(_, evaluationUuid) {
      try {
        const response = await ApiService.deleteRequest(`${evaluationUuid}`)
        if (response.status === 204) {
          Notify.create({
            message: "L'évaluation a été supprimée avec succès.",
            color: 'green'
          })
          return Promise.resolve()
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Une erreur s'est produite",
          color: 'red'
        })
        return Promise.reject(error)
      }
    }
  },

  getters: {
    getEvaluation(state) {
      return state.evaluation
    },
    getEvaluations(state) {
      return state.evaluations
    }
  }
}
